import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { format, subDays } from "date-fns";

interface MoodData {
  date: string;
  mood_score: number;
  note?: string;
  emoji?: string;
}

export function MoodHistory() {
  const [moodData, setMoodData] = useState<MoodData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const fetchMoodHistory = async () => {
      try {
        const thirtyDaysAgo = subDays(new Date(), 30).toISOString();
        const { data, error } = await supabase
          .from('daily_moods')
          .select('*')
          .gte('date', thirtyDaysAgo)
          .order('date', { ascending: true });

        if (error) throw error;

        setMoodData(data || []);
      } catch (error: any) {
        console.error('Error fetching mood history:', error);
        toast({
          title: "Error loading mood history",
          description: "Please try again later",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchMoodHistory();
  }, [toast]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Card className="p-3 bg-background border shadow-lg">
          <p className="text-sm font-medium">{format(new Date(label), 'MMM d, yyyy')}</p>
          <p className="text-sm">Mood: {data.emoji} ({data.mood_score}/5)</p>
          {data.note && <p className="text-sm text-muted-foreground mt-1">{data.note}</p>}
        </Card>
      );
    }
    return null;
  };

  if (isLoading) {
    return <div>Loading mood history...</div>;
  }

  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold mb-4">Your Mood History</h3>
      <div className="h-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={moodData}>
            <XAxis 
              dataKey="date" 
              tickFormatter={(date) => format(new Date(date), 'MMM d')}
            />
            <YAxis domain={[1, 5]} ticks={[1, 2, 3, 4, 5]} />
            <Tooltip content={<CustomTooltip />} />
            <Line
              type="monotone"
              dataKey="mood_score"
              stroke="hsl(var(--primary))"
              strokeWidth={2}
              dot={{ fill: "hsl(var(--primary))" }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}